import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ArrowRight, CalendarDays } from "lucide-react";

const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeTag, setActiveTag] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Blog — SphereHyx | Sports Club Management Tips";
    const md = document.querySelector('meta[name="description"]');
    if (md) md.setAttribute("content", "Guides, tips and stories on running a sports club — attendance, payments, tournaments and coaching. From the SphereHyx team.");
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/public${activeTag ? `?tag=${encodeURIComponent(activeTag)}` : ""}`)
      .then((r) => { setPosts(r.data.posts || []); if (!activeTag) setTags(r.data.tags || []); })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [activeTag]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="blog-page">
      <header className="flex items-center justify-between px-6 py-5 max-w-6xl mx-auto">
        <Link to="/" data-testid="blog-logo-link"><Logo /></Link>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" className="text-muted-foreground hover:text-foreground"><Link to="/login" data-testid="blog-login-btn">Log in</Link></Button>
          <Button asChild className="brand-gradient text-white border-0 rounded-full px-5 hover:opacity-90"><Link to="/register" data-testid="blog-signup-btn">Start free <ArrowRight size={16} className="ml-1" /></Link></Button>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-10 pb-6">
        <p className="uppercase tracking-[0.25em] text-xs text-secondary font-semibold mb-3">SphereHyx Blog</p>
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-tight">Run a better sports club</h1>
        <p className="text-base md:text-lg text-muted-foreground mt-4 max-w-2xl">Practical guides on attendance, payments, tournaments and coaching — plus product news from the SphereHyx team.</p>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-7" data-testid="blog-tags">
            <button onClick={() => setActiveTag("")} className={`h-8 px-3.5 rounded-full text-sm border transition-colors ${!activeTag ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`} data-testid="blog-tag-all">All</button>
            {tags.map((t) => (
              <button key={t} onClick={() => setActiveTag(t)} className={`h-8 px-3.5 rounded-full text-sm border transition-colors ${activeTag === t ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`} data-testid={`blog-tag-${t}`}>{t}</button>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20">
        {loading ? (
          <p className="text-muted-foreground py-16 text-center" data-testid="blog-loading">Loading posts…</p>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border rounded-2xl" data-testid="blog-empty">
            <p className="font-heading font-semibold text-lg">No posts yet</p>
            <p className="text-muted-foreground text-sm mt-1">Check back soon — great content is on the way.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {posts.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="group rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/50 transition-colors flex flex-col" data-testid={`blog-card-${p.slug}`}>
                <div className="aspect-[16/9] bg-muted overflow-hidden">
                  {p.cover_image ? (
                    <img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full brand-gradient opacity-80" />
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  {p.tags?.length > 0 && <p className="text-[11px] uppercase tracking-wider text-secondary font-semibold mb-2">{p.tags[0]}</p>}
                  <h2 className="font-heading font-bold text-lg leading-snug group-hover:text-primary transition-colors">{p.title}</h2>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-3 flex-1">{p.excerpt}</p>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-4"><CalendarDays size={13} /> {fmtDate(p.published_at)} · {p.author_name}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-border py-10 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <Logo />
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link>
            <a href="/privacy" className="hover:text-foreground transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-foreground transition-colors">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
